// client/src/pages/Recovery/components/RecoveryTimelineSlider.js
// 21-dagars slider med draggable symptompunkter. Renderar "Idag"-markör
// och hanterar drag-validering mot tidigare punkter.
import React, { useState, useRef, useEffect } from 'react';
import DraggablePoint from '../../UserReports/components/DraggablePoint';

const RecoveryTimelineSlider = ({ 
  timelinePoints = [], 
  onPointsChange, 
  startDate = null
}) => {
  
  const TIMELINE_DAYS = 21;
  const START_POSITION = 10; // Flyttat från 7 till 10 för bättre balans
  const SLIDER_WIDTH = 1100;
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragPointId, setDragPointId] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const timelineRef = useRef(null);
  
  const baseDate = startDate ? new Date(startDate) : new Date();
  const timelineDates = Array.from({ length: TIMELINE_DAYS }, (_, index) => {
    const date = new Date(baseDate);
    const offsetDays = index - START_POSITION;
    date.setDate(baseDate.getDate() + offsetDays);
    return date;
  });
  
  // ✅ Calculate "Idag" position
  const getTodayPosition = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let closestPosition = -1;
    let minDiff = Infinity;
    
    timelineDates.forEach((timelineDate, index) => {
      const diff = Math.abs(today - timelineDate);
      if (diff < minDiff) {
        minDiff = diff;
        closestPosition = index;
      }
    });
    
    // Only show if today is within timeline range (allow 1 day tolerance)
    const oneDayMs = 24 * 60 * 60 * 1000;
    if (minDiff <= oneDayMs) {
      return closestPosition;
    }
    return -1; // Not visible on timeline
  };
  
  const todayPosition = getTodayPosition();
  
  const dateToPosition = (dateString) => {
    const targetDate = new Date(dateString);
    let closestPosition = START_POSITION;
    let minDiff = Infinity;
    
    timelineDates.forEach((timelineDate, index) => {
      const diff = Math.abs(targetDate - timelineDate);
      if (diff < minDiff) {
        minDiff = diff;
        closestPosition = index;
      }
    });
    
    return closestPosition;
  };
  
  const positionToDate = (position) => {
    const clampedPosition = Math.max(0, Math.min(TIMELINE_DAYS - 1, Math.round(position)));
    const selectedDate = timelineDates[clampedPosition];
    return selectedDate.toISOString().split('T')[0];
  };
  
  const mouseXToPosition = (mouseX) => {
    if (!timelineRef.current) return START_POSITION;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const relativeX = mouseX - rect.left;
    const position = (relativeX / rect.width) * (TIMELINE_DAYS - 1);
    
    return Math.max(0, Math.min(TIMELINE_DAYS - 1, position));
  };
  
  const positionToFieldCenterPercentage = (position) => {
    const fieldWidth = 100 / TIMELINE_DAYS;
    const fieldCenterPercentage = (position + 0.5) * fieldWidth;
    return Math.min(100, fieldCenterPercentage);
  };
  
  // ✅ FINAL: Validering enligt §1 och §2
  const validatePointPosition = (pointId, newPosition, currentPoints) => {
    const point = currentPoints.find(p => p.id === pointId);
    if (!point) return false;
    
    const newDate = new Date(positionToDate(newPosition));
    newDate.setHours(0, 0, 0, 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const otherPoints = currentPoints.filter(p => p.id !== pointId);
    
    // Global bounds: -7 till +14 dagar från startdatum
    const maxDate = new Date(baseDate);
    maxDate.setDate(baseDate.getDate() + 14);
    if (newDate > maxDate) return false;
    
    const minDate = new Date(baseDate);
    minDate.setDate(baseDate.getDate() - 7);
    if (newDate < minDate) return false;
    
    // Hitta andra punkter
    const insjuknandePoint = otherPoints.find(p => p.type === 'insjuknande');
    const nyaSymptomPoints = otherPoints.filter(p => p.type === 'nya_symptom');
    const friskPoint = otherPoints.find(p => p.type === 'frisk');
    
    switch (point.type) {
      case 'insjuknande':
        // Insjuknande måste vara före idag
        if (newDate >= today) return false;
        // Måste vara FÖRE alla nya_symptom och frisk
        for (const p of [...nyaSymptomPoints, friskPoint].filter(Boolean)) {
          const pDate = new Date(p.date);
          pDate.setHours(0, 0, 0, 0);
          if (newDate >= pDate) return false;
        }
        return true;
        
      case 'nya_symptom':
        // §1: Max gårdagens datum (strikt före idag)
        if (newDate > yesterday) return false;
        // Min: dagen EFTER insjuknande
        if (insjuknandePoint) {
          const insjDate = new Date(insjuknandePoint.date);
          insjDate.setHours(0, 0, 0, 0);
          if (newDate <= insjDate) return false;
        }
        // Måste vara FÖRE frisk
        if (friskPoint) {
          const friskDate = new Date(friskPoint.date);
          friskDate.setHours(0, 0, 0, 0);
          if (newDate >= friskDate) return false;
        }
        return true;
        
      case 'frisk':
        // §2: Max dagens datum
        if (newDate > today) return false;
        // Min: dagen EFTER senaste punkt (insjuknande eller nya_symptom)
        let latestOtherDate = null;
        if (insjuknandePoint) {
          latestOtherDate = new Date(insjuknandePoint.date);
        }
        for (const p of nyaSymptomPoints) {
          const pDate = new Date(p.date);
          if (!latestOtherDate || pDate > latestOtherDate) {
            latestOtherDate = pDate;
          }
        }
        if (latestOtherDate) {
          latestOtherDate.setHours(0, 0, 0, 0);
          if (newDate <= latestOtherDate) return false;
        }
        return true;
        
      default:
        return true;
    }
  };
  
  const handlePointDragStart = (pointId, startX) => {
    setIsDragging(true);
    setDragPointId(pointId);
    setHoveredPoint(null);
  };
  
  const handlePointDrag = (pointId, currentX) => {
    if (!isDragging || dragPointId !== pointId) return;
    
    const newPosition = mouseXToPosition(currentX);
    const newDate = positionToDate(newPosition);
    
    if (!validatePointPosition(pointId, newPosition, timelinePoints)) return;
    
    const updatedPoints = timelinePoints.map(point => 
      point.id === pointId ? { ...point, date: newDate } : point
    );
    
    onPointsChange && onPointsChange(updatedPoints);
  };
  
  const handlePointDragEnd = (pointId) => {
    setIsDragging(false);
    setDragPointId(null);
  };
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && dragPointId) {
        handlePointDrag(dragPointId, e.clientX);
      }
    };
    
    const handleMouseUp = () => {
      if (isDragging) {
        handlePointDragEnd(dragPointId);
      }
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragPointId]);
  
  const getPointColor = (type) => {
    switch (type) {
      case 'insjuknande': return '#FF453A'; // Apple red
      case 'nya_symptom': return '#FF9F0A'; // Apple orange
      case 'frisk': return '#30D158';       // Apple green
      default: return 'rgba(255, 255, 255, 0.4)';
    }
  };
  
  const formatDateForTooltip = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Idag';
    if (diffDays === 1) return 'Imorgon';
    if (diffDays === -1) return 'Igår';
    if (diffDays > 0) return `Om ${diffDays} dagar`;
    if (diffDays < 0) return `${Math.abs(diffDays)} dagar sedan`;
    
    return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' });
  };
  
  const getPointDisplayName = (type) => {
    switch (type) {
      case 'insjuknande': return 'Insjuknande';
      case 'nya_symptom': return 'Nya symptom';
      case 'frisk': return 'Frisk';
      default: return 'Okänd';
    }
  };
  
  const formatSymptomsForTooltip = (symptoms) => {
    if (!symptoms || symptoms.length === 0) return 'Inga symptom';
    if (symptoms.length <= 2) return symptoms.join(', ');
    return `${symptoms.slice(0, 2).join(', ')} +${symptoms.length - 2}`;
  };
  
  return (
    <div className="recovery-timeline-container">
      <div 
        className="timeline-track"
        ref={timelineRef}
        style={{ width: `${SLIDER_WIDTH}px` }}
      >
        <div className="timeline-line"></div>
        
        {/* ✅ "Idag" marker - svävar ovanför allt */}
        {todayPosition >= 0 && (
          <div 
            className="today-marker"
            style={{ left: `${positionToFieldCenterPercentage(todayPosition)}%` }}
          >
            <span className="today-label">idag</span>
            <div className="today-line"></div>
          </div>
        )}
        
        {timelinePoints.map((point) => {
          const position = dateToPosition(point.date);
          const leftPercentage = positionToFieldCenterPercentage(position);
          
          return (
            <div 
              key={point.id} 
              className="timeline-point-container"
              style={{ 
                position: 'absolute',
                left: `${leftPercentage}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: isDragging && dragPointId === point.id ? 1000 : 10
              }}
              onMouseEnter={() => !isDragging && setHoveredPoint(point.id)}
              onMouseLeave={() => setHoveredPoint(null)}
            >
              <DraggablePoint
                id={point.id}
                type={point.type}
                date={point.date}
                symptoms={point.symptoms}
                position={0}
                color={getPointColor(point.type)}
                isDragging={isDragging && dragPointId === point.id}
                onDragStart={handlePointDragStart}
                onDrag={handlePointDrag}
                onDragEnd={handlePointDragEnd}
                compact={true}
              />
              
              <div 
                className="point-date-label"
                style={{ color: getPointColor(point.type) }}
              >
                {new Date(point.date).getDate()}&nbsp;
                {new Date(point.date).toLocaleDateString('sv-SE', { month: 'short' })}
              </div>
              
              {point.symptoms && point.symptoms.length > 0 && (
                <>
                  <div 
                    className="point-symptoms-connector"
                    style={{ backgroundColor: getPointColor(point.type) }}
                  />
                  <div 
                    className="point-symptoms-display"
                    style={{ color: getPointColor(point.type) }}
                  >
                    {point.symptoms.join(', ')}
                  </div>
                </>
              )}
              
              {hoveredPoint === point.id && !isDragging && (
                <div className="point-tooltip">
                  <div className="tooltip-arrow"></div>
                  <div className="tooltip-content">
                    <div className="tooltip-title">{getPointDisplayName(point.type)}</div>
                    <div className="tooltip-date">{formatDateForTooltip(point.date)}</div>
                    <div className="tooltip-symptoms">{formatSymptomsForTooltip(point.symptoms)}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      <style>{`
        /* ============================================
           🎨 APPLE DARK MODE TIMELINE
           ============================================ */
        .recovery-timeline-container {
          position: relative;
          width: 100%;
          max-width: 1200px;
          height: 280px;
          margin: 0 auto;
          padding: 20px 0;
          background: transparent;
          overflow: visible;
        }
        
        .timeline-track {
          position: relative;
          height: 200px;
          margin: 0 auto;
          padding: 12px 0;
          width: ${SLIDER_WIDTH}px;
          max-width: 100%;
          background: transparent;
          overflow: visible;
        }
        
        /* ✅ Dark timeline line with subtle glow */
        .timeline-line {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 12px;
          transform: translateY(-50%);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.08) 0%,
            rgba(255, 255, 255, 0.12) 50%,
            rgba(255, 255, 255, 0.08) 100%
          );
          border-radius: 6px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          box-shadow: 
            inset 0 1px 2px rgba(255, 255, 255, 0.1),
            inset 0 -1px 2px rgba(0, 0, 0, 0.3),
            0 0 20px rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        /* ✅ "Idag" marker - svävar ovanför allt */
        .today-marker {
          position: absolute;
          top: 0;
          transform: translateX(-50%);
          z-index: 5;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .today-label {
          font-size: 10px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
        }
        
        .today-line {
          width: 1px;
          height: 65px;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.4) 0%,
            rgba(255, 255, 255, 0.2) 60%,
            transparent 100%
          );
        }
        
        .timeline-point-container {
          cursor: pointer;
        }
        
        /* ✅ Dark text shadows */
        .point-date-label {
          position: absolute;
          top: -54px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.7rem;
          font-weight: 600;
          white-space: nowrap;
          z-index: 20;
          pointer-events: none;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
        }
        
        .point-symptoms-connector {
          position: absolute;
          top: 25px;
          left: 50%;
          transform: translateX(-50%);
          width: 1px;
          height: 35px;
          opacity: 0.4;
          pointer-events: none;
          z-index: 4;
        }

        /* ✅ Dark text shadows for symptoms */
        .point-symptoms-display {
          position: absolute;
          top: 65px;
          left: -5px;
          transform: rotate(-45deg);
          transform-origin: top left;
          font-size: 0.65rem;
          font-weight: 500;
          white-space: normal;
          word-wrap: break-word;
          pointer-events: none;
          z-index: 5;
          max-width: 200px;
          line-height: 1.2;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.9);
          opacity: 0.9;
        }
        
        /* ✅ Apple-style glassmorphism tooltip */
        .point-tooltip {
          position: absolute;
          top: 120px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 50;
          pointer-events: none;
          animation: tooltipSlideIn 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
        }
        
        @keyframes tooltipSlideIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-15px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        
        .tooltip-arrow {
          position: absolute;
          top: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid rgba(28, 28, 30, 0.95);
        }
        
        .tooltip-content {
          background: rgba(28, 28, 30, 0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          color: #F5F5F7;
          border-radius: 12px;
          padding: 1rem 1.2rem;
          box-shadow: 
            0 12px 35px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.1);
          min-width: 200px;
          max-width: 280px;
          text-align: center;
        }
        
        .tooltip-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: #F5F5F7;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .tooltip-date {
          font-size: 0.8rem;
          color: #007AFF;
          margin-bottom: 0.3rem;
          font-weight: 500;
        }
        
        .tooltip-symptoms {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.6);
        }
        
        /* ============================================
           📱 RESPONSIVE
           ============================================ */
        @media (max-width: 768px) {
          .recovery-timeline-container { height: 300px; }
          .point-symptoms-display { font-size: 0.6rem; top: 60px; }
          .today-label { font-size: 9px; }
          .today-line { height: 55px; }
        }
      `}</style>
    </div>
  );
};

export default RecoveryTimelineSlider;
